import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '../components/UIComponents';
import { CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { AppMark } from '../components/Logo';
import { forgotPassword } from '../api/auth';
import { handleApiError } from '../api/client';
import { useTranslation } from 'react-i18next';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await forgotPassword({ email });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setSubmitted(false);
    setLoading(true);
    setError('');

    try {
      await forgotPassword({ email });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] py-12 px-4">
        <Card className="w-full max-w-md p-8 text-center space-y-6 !bg-card !ring-0 !shadow-none !rounded-[28px]">
           <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
           </div>
           <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('auth.checkEmail')}</h2>
           <p className="text-slate-600 dark:text-zinc-400">
             {t('auth.resetEmailDescription', { email })}
           </p>
           <Button className="w-full" variant="outline" onClick={() => window.location.href = '#/login'}>
             {t('auth.backToLogin')}
           </Button>
           <p className="text-sm text-slate-500 dark:text-zinc-400">
             {t('auth.didNotReceive')} <button className="text-indigo-600 font-medium hover:underline" onClick={handleResend}>{t('auth.clickToResend')}</button>
           </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
             <AppMark size={56} />
          </Link>
          <h2 className="h-section">
            {t('auth.resetPassword')}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
            {t('auth.resetPasswordDescription')}
          </p>
        </div>

        <Card className="p-8 !bg-card !ring-0 !shadow-none !rounded-[28px]">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                {t('common.email')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block h-11 w-full rounded-[12px] border border-transparent bg-raised px-3.5 text-[15px] text-ink placeholder:text-ink-3 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06)] focus:border-transparent focus:outline-none focus:ring-[3px] focus:ring-accent/30 transition-shadow dark:bg-white/[0.06]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" isLoading={loading}>
              {t('auth.sendResetLink')}
            </Button>
          </form>

          <div className="mt-6 text-center">
             <Link to="/login" className="inline-flex items-center text-sm font-medium text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white">
                <ArrowLeft className="h-4 w-4 me-2" />
                {t('auth.backToLogin')}
             </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
